import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

// Component imports
import BidDetailsForm from './BidDetailsForm';
import FileUploadForm from './FileUploadForm';
import EmailNotificationForm from './EmailNotificationForm';
import DotWave from 'src/components/DotWave';

// Updated BidFormData interface to include the award-related fields
export interface BidFormData {
  bidType: string;
  contractNo: string;
  title: string;
  categories: string[];
  department: string;
  adDate: Date | null;
  openingDate: Date | null;
  effectiveDate: Date | null;
  status: string;
  description: string;
  adminNotes: string;
  awardedToContactId: number;
  poNumber: string;
  // Additional fields for bid result and PO files
  awardedToVendor?: string;
  awardedToContact?: string;
  bidResult?: File | null;
  bidResultUrl?: string | null;
  bidResultTitle?: string;
  bidResultUploaded?: boolean;
  bidResultError?: string | null;
  bidResultIsUploading?: boolean;
  bidResultDocId?: string; // Document ID for existing bid result file
  poFile?: File | null;
  poFileUrl?: string | null;
  poFileTitle?: string;
  poFileUploaded?: boolean;
  poFileError?: string | null;
  poFileIsUploading?: boolean;
  poFileDocId?: string; // Document ID for existing PO file
  awardAmount?: string;
  isAddendum: boolean;
  reopenAward?: boolean; // checkbox
  reopenReason?: string; // required when checkbox is ON
}

// Updated BidFormErrors interface to include award-related error fields
export interface BidFormErrors {
  bidType?: string;
  contractNo?: string;
  title?: string;
  categories?: string;
  department?: string;
  openingDate?: string;
  adDate?: string;
  status?: string;
  awardedToVendor?: string;
  awardedToContact?: string;
  bidResult?: string;
  poNumber?: string;
  poFile?: string;
  saveType?: string;
  subject?: string;
  reopenReason?: string;

  content?: string;
}

export interface UploadedFile {
  title: string;
  file: File | null;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  id?: string;
  isUploading?: boolean;
  uploaded?: boolean;
  uploadError?: string;
}

export interface EmailNotification {
  subject: string;
  content: string;
  saveType: string;
}

// Dropdown data interfaces
export interface TypeOption {
  TypeID: number;
  Type: string;
  Active: boolean;
}

export interface CategoryOption {
  CategoryID: number;
  CategoryName: string;
}

export interface DepartmentOption {
  DepartmentID: number;
  DepartmentName: string;
}

export interface StatusOption {
  StatusID: number;
  Status: string;
  Active: boolean;
}

const BidUpdationForm: React.FC = () => {
  const { bidId } = useParams<{ bidId: string }>();
  const theme = useTheme();
  const navigate = useNavigate();
  const { state: locationState, township, uuid } = useParams();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bidDataLoaded, setBidDataLoaded] = useState(false);
  const [isGeneratingContractNo, setIsGeneratingContractNo] = useState(false);

  // NEW: Track the original status from API to determine if form should be disabled
  const [originalStatus, setOriginalStatus] = useState<string>('');

  // State for API data
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  // State to store the raw bid data
  const [rawBidData, setRawBidData] = useState<any>(null);

  const [formData, setFormData] = useState<BidFormData>({
    bidType: '',
    contractNo: '',
    title: '',
    categories: [],
    department: '',
    adDate: null,
    openingDate: null,
    effectiveDate: null,
    status: '',
    description: '',
    adminNotes: '',
    awardedToContactId: null,
    poNumber: '',
    // Additional fields for award-related data
    awardedToVendor: '',
    awardedToContact: '',
    bidResult: null,
    bidResultUrl: null,
    bidResultTitle: '',
    bidResultUploaded: false,
    bidResultError: null,
    bidResultIsUploading: false,
    bidResultDocId: undefined,
    poFile: null,
    poFileUrl: null,
    poFileTitle: '',
    poFileUploaded: false,
    poFileError: null,
    poFileIsUploading: false,
    poFileDocId: undefined,
    awardAmount: null,
    isAddendum: false,
    reopenAward: false,
    reopenReason: ''
  });

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([
    {
      title: 'Original Package',
      file: null
    }
  ]);

  const [emailData, setEmailData] = useState<EmailNotification>({
    subject: '',
    content: '',
    saveType: ''
  });

  const [errors, setErrors] = useState<BidFormErrors>({});

  const steps = ['Bid Details', 'Upload Files', 'Email Notification'];

  // Fetch all dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingDropdowns(true);
      try {
        // Fetch types
        const typesResponse = await httpRequest(
          'GET',
          `/bid_portal/type/get_all`,
          {},
          {}
        );

        if (typesResponse.status === 'success') {
          // Check both data and Data properties
          const typesData =
            typesResponse.data?.data || typesResponse.data?.Data || [];
          setTypes(typesData);
        } else {
          console.error('Failed to fetch types:', typesResponse);
        }

        // Fetch categories
        const categoriesResponse = await httpRequest(
          'GET',
          `/bid_portal/category/get_all`,
          {},
          {}
        );

        if (categoriesResponse.status === 'success') {
          const categoriesData =
            categoriesResponse.data?.data ||
            categoriesResponse.data?.Data ||
            [];
          setCategories(categoriesData);
        } else {
          console.error('Failed to fetch categories:', categoriesResponse);
        }

        // Fetch departments
        const departmentsResponse = await httpRequest(
          'GET',
          '/bid_portal/department/get_all',
          {},
          {}
        );

        if (departmentsResponse.status === 'success') {
          const departmentsData =
            departmentsResponse.data?.data ||
            departmentsResponse.data?.Data ||
            [];
          setDepartments(departmentsData);
        } else {
          console.error('Failed to fetch departments:', departmentsResponse);
        }

        // Fetch statuses
        const statusesResponse = await httpRequest(
          'GET',
          `/bid_portal/status/get_all`,
          {},
          {}
        );

        if (statusesResponse.status === 'success') {
          const statusesData =
            statusesResponse.data?.data || statusesResponse.data?.Data || [];
          setStatuses(statusesData);
        } else {
          console.error('Failed to fetch statuses:', statusesResponse);
        }
      } catch (errFetch) {
        console.error('Error fetching dropdown data:', errFetch);
        setError('Failed to load form options. Please try again.');
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch bid details from API
  useEffect(() => {
    let isMounted = true;

    const fetchBidDetails = async () => {
      if (!bidId) {
        setError('Bid ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await httpRequest(
          'GET',
          `/bid_portal/bid_details/get?BidID=${bidId}`,
          {},
          {}
        );

        // Guard against state updates after unmount
        if (!isMounted) return;

        if (
          response.type === 'NETWORK_ERROR' ||
          response.status !== 'success'
        ) {
          throw new Error(response.message || 'Failed to fetch bid details');
        }

        // Process the response and populate the form
        // Find the correct bid from the data array - check both data and Data properties
        const dataArray = response.data?.data || response.data?.Data || [];
        const bidData = dataArray.find(
          (bid: any) => bid.BidID?.toString() === bidId
        );

        // Store raw bid data for processing
        setRawBidData(bidData);

        if (bidData) {
          setBidDataLoaded(true);
        } else {
          console.error('Bid data not found in response');
          setError('Bid details not found');
        }
      } catch (fetchErr: any) {
        if (isMounted) {
          console.error('Error fetching bid details:', fetchErr);
          setError(fetchErr.message || 'Failed to fetch bid details');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchBidDetails();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [bidId]);

  // Process bid data once both bid data and dropdowns are loaded
  useEffect(() => {
    if (
      bidDataLoaded &&
      !loadingDropdowns &&
      rawBidData &&
      types.length > 0 &&
      departments.length > 0 &&
      statuses.length > 0
    ) {
      try {
        // Find the type name - check if TypeID exists
        const typeName =
          rawBidData.TypeID !== undefined
            ? types.find((t) => t.TypeID === rawBidData.TypeID)?.Type || ''
            : '';

        // Find department name
        let departmentName = '';
        if (rawBidData.DepartmentID !== undefined) {
          const dept = departments.find(
            (d) => d.DepartmentID === rawBidData.DepartmentID
          );
          departmentName = dept?.DepartmentName || '';
        }

        // Find status name
        const statusName =
          rawBidData.StatusID !== undefined
            ? statuses.find((s) => s.StatusID === rawBidData.StatusID)
                ?.Status || ''
            : '';

        // STORE THE ORIGINAL STATUS FROM API
        setOriginalStatus(statusName);

        // Map CategoryIDs to category names
        let categoryNames: string[] = [];
        if (rawBidData.Categories && Array.isArray(rawBidData.Categories)) {
          categoryNames = rawBidData.Categories.map(
            (cat: any) => cat.CategoryName || ''
          ).filter((name: string) => name !== '');
        } else if (
          rawBidData.CategoryIDs &&
          Array.isArray(rawBidData.CategoryIDs)
        ) {
          categoryNames = rawBidData.CategoryIDs.map((catId: number) => {
            const category = categories.find((c) => c.CategoryID === catId);
            return category ? category.CategoryName : '';
          }).filter((name: string) => name !== '');
        }

        // FIXED: Handle vendor and contact information correctly
        let awardedToVendor = '';
        let awardedToContact = '';

        // Check if AwardedToVendor is an object or string/null
        if (rawBidData.AwardedToVendor) {
          if (
            typeof rawBidData.AwardedToVendor === 'object' &&
            rawBidData.AwardedToVendor.Business
          ) {
            awardedToVendor = rawBidData.AwardedToVendor.Business;
          } else if (typeof rawBidData.AwardedToVendor === 'string') {
            awardedToVendor = rawBidData.AwardedToVendor;
          }
        }

        // Check if AwardedToContact is an object or string/null
        if (rawBidData.AwardedToContact) {
          if (typeof rawBidData.AwardedToContact === 'object') {
            // Build contact display name from object properties
            const firstName = rawBidData.AwardedToContact.FirstName || '';
            const lastName = rawBidData.AwardedToContact.LastName || '';
            const email = rawBidData.AwardedToContact.EmailID || '';

            // Create display string similar to what the API returns
            if (firstName || lastName || email) {
              awardedToContact = `${firstName} ${lastName} - ${email}`.trim();
              // Clean up any extra spaces or dashes
              awardedToContact = awardedToContact
                .replace(/\s+-\s*$/, '')
                .replace(/^\s*-\s+/, '');
            }
          } else if (typeof rawBidData.AwardedToContact === 'string') {
            awardedToContact = rawBidData.AwardedToContact;
          }
        }

        // Prepare initial form data from rawBidData
        let initialFormData = {
          bidType: typeName,
          contractNo: rawBidData.ContractNo || '',
          title: rawBidData.Title || '',
          categories: categoryNames,
          department: departmentName,
          adDate: rawBidData.AdDate ? new Date(rawBidData.AdDate) : null,
          openingDate: rawBidData.OpeningDate
            ? new Date(rawBidData.OpeningDate)
            : null,
          effectiveDate: rawBidData.EffectiveDate
            ? new Date(rawBidData.EffectiveDate)
            : null,
          status: statusName,
          description: rawBidData.Description || '',
          adminNotes: rawBidData.AdminNote || '',
          awardedToContactId: rawBidData.AwardedTo || 0,
          poNumber: rawBidData.PONumber || '',
          awardedToVendor: awardedToVendor,
          awardedToContact: awardedToContact,
          bidResult: null,
          bidResultUrl: null,
          bidResultTitle: '',
          bidResultUploaded: false,
          bidResultError: null,
          bidResultIsUploading: false,
          bidResultDocId: undefined,
          poFile: null,
          poFileUrl: null,
          poFileTitle: '',
          poFileUploaded: false,
          poFileError: null,
          poFileIsUploading: false,
          poFileDocId: undefined,
          awardAmount: rawBidData.AwardAmount,
          isAddendum: false
        };

        // SEPARATE REGULAR DOCUMENTS FROM SPECIAL DOCUMENTS
        const regularFiles: any[] = [];

        // Change from DocumentFileUpload to Documents based on your API response
        if (
          rawBidData.Documents &&
          Array.isArray(rawBidData.Documents) &&
          rawBidData.Documents.length > 0
        ) {
          rawBidData.Documents.forEach((file: any) => {
            const fileDescription =
              file.DocumentDescription || file.Description || '';
            const fileName = file.DocumentName || file.FileName || '';
            const fileType = file.DocumentType || file.Type || '';
            const fileUrl = file.DocumentUrl || `/api/documents/${fileName}`;
            const bidDocsId = file.BidDocsID?.toString() || '';

            // Check if this is a special document (Bid Result or PO) based on description
            const isBidResultDoc = fileDescription
              .toLowerCase()
              .includes('bid result');
            const isPODoc = fileDescription
              .toLowerCase()
              .includes('po document');

            if (isBidResultDoc) {
              // This is a Bid Result document - add to the form data
              initialFormData = {
                ...initialFormData,
                bidResultTitle: fileDescription,
                bidResultUrl: fileUrl,
                bidResultUploaded: true,
                bidResultDocId: bidDocsId
              };
            } else if (isPODoc) {
              // This is a PO document - add to the form data
              initialFormData = {
                ...initialFormData,
                poFileTitle: fileDescription,
                poFileUrl: fileUrl,
                poFileUploaded: true,
                poFileDocId: bidDocsId
              };
            } else {
              // This is a regular document - add to the regular files array for Step 2
              regularFiles.push({
                title: fileDescription,
                file: null,
                fileUrl: fileUrl,
                fileName: fileName,
                fileType: fileType,
                id: bidDocsId,
                uploaded: true
              });
            }
          });
        }

        // Set the form data with all the processed information
        setFormData(initialFormData);

        // Set the uploaded files array with ONLY regular files (not bid result or PO files)
        if (regularFiles.length > 0) {
          setUploadedFiles(regularFiles);
        } else {
          // If no regular files, ensure we have at least one empty slot for "Original Package"
          setUploadedFiles([
            {
              title: 'Original Package',
              file: null
            }
          ]);
        }
      } catch (processErr) {
        console.error('Error processing bid data:', processErr);
        setError(
          'Error processing bid data. Please check console for details.'
        );
      }
    }
  }, [
    bidDataLoaded,
    loadingDropdowns,
    rawBidData,
    types,
    departments,
    statuses,
    categories
  ]);

  const handleBidTypeChange = async (selectedType: string) => {
    // First update the form data with the selected type
    setFormData({ ...formData, bidType: selectedType });

    // Find the TypeID for the selected type
    const selectedTypeObj = types.find((type) => type.Type === selectedType);

    if (selectedTypeObj?.TypeID) {
      try {
        // Show loading state
        setIsGeneratingContractNo(true);
        // Call the API to generate a contract number based on the type
        const response = await httpRequest(
          'GET',
          `/bid_portal/bid_details/generate_contract_number?Type=${selectedTypeObj.TypeID}`,
          {},
          {}
        );
        if (
          response.type === 'NETWORK_ERROR' ||
          response.status !== 'success'
        ) {
          throw new Error(
            response.message || 'Failed to generate contract number'
          );
        }

        // Extract the contract number from the response
        const generatedContractNo =
          response.data?.ContractNo || response.data?.data?.ContractNumber;

        if (generatedContractNo) {
          // Update the form with the generated contract number
          setFormData((prevData) => ({
            ...prevData,
            contractNo: generatedContractNo
          }));

          toast.success('Contract number generated successfully');
        } else {
          throw new Error('Contract number not found in the response');
        }
      } catch (err: any) {
        console.error('Error generating contract number:', err);
        toast.error(err.message || 'Failed to generate contract number');
      } finally {
        setIsGeneratingContractNo(false);
      }
    }
  };

  // UPDATED: Check if form should be disabled based on ORIGINAL status from API
  const isFormDisabled =
    originalStatus === 'Awarded' ||
    originalStatus === 'Close' ||
    originalStatus === 'Closed';

  const overrideEnabled = !!formData.reopenAward;
  const uiDisabled = isFormDisabled && !overrideEnabled;

  function isEmptyContent(html) {
    // Remove all HTML tags and whitespace
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text.length === 0;
  }

  const validateForm = () => {
    const newErrors: BidFormErrors = {};

    // Only skip validation if still truly disabled (override not active)
    if (uiDisabled) {
      return true;
    }

    // Existing validations...
    if (!formData.bidType) newErrors.bidType = 'Type is required';
    if (!formData.contractNo)
      newErrors.contractNo = 'Contract number is required';
    if (!formData.title) newErrors.title = 'Title is required';
    if (formData.categories.length === 0)
      newErrors.categories = 'At least one category is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.openingDate)
      newErrors.openingDate = 'Opening Date is required';
    if (!formData.status) newErrors.status = 'Status is required';
    if (!formData.adDate) newErrors.adDate = 'Ad Date is required';

    if (formData.status === 'Awarded') {
      if (!formData.awardedToVendor)
        newErrors.awardedToVendor = 'Awarded To (Vendor) is required';
      if (!formData.awardedToContact)
        newErrors.awardedToContact = 'Awarded To (Contact) is required';
    }

    // NEW: require Reason when Re-Open/Award is checked
    if (formData.reopenAward && !formData.reopenReason?.trim() && formData.status === 'Open') {
      newErrors.reopenReason = 'Reason to Re-Open is required';
    }

    if (activeStep === 2) {
      if (!emailData.subject) newErrors.subject = 'Email subject is required';
      if (!emailData.saveType)
        newErrors.saveType = 'Notification option is required';
      if (!emailData.content || isEmptyContent(emailData.content)) {
        newErrors.content = 'Email content is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateForm()) {
      return;
    }

    const hasUploadError = uploadedFiles.some((item) => !item.uploaded);
    if (activeStep === 1 && hasUploadError) return;

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFileUpload = async (index: number, file: File) => {
    if (!bidId) {
      setError('Bid ID is missing');
      return;
    }

    // Update the file in state with the response data
    const updatedFiles = [...uploadedFiles];
    updatedFiles[index] = {
      ...updatedFiles[index],
      file: file,
      isUploading: false,
      uploaded: true,
      uploadError: undefined,
      id: ''
    };
    setUploadedFiles(updatedFiles);
    setError(null);
  };

  const handleFileUploadForPOAndBidResult = async (
    index: number,
    file: File
  ) => {
    if (!bidId) {
      setError('Bid ID is missing');
      return;
    }

    // Update UI to show selected file immediately
    const newFiles = [...uploadedFiles];
    const currentFile = newFiles[index];

    try {
      // Create FormData for the file
      const uploadFormData = new FormData();
      uploadFormData.append('BidID', bidId);
      uploadFormData.append('UploadBidsDocument', file);
      uploadFormData.append(
        'DocumentDescription',
        currentFile?.title || `File ${index + 1}`
      );

      // Upload the file immediately
      const response = await httpRequest(
        'POST',
        '/bid_portal/bids/upload_document',
        uploadFormData,
        {
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response.type === 'NETWORK_ERROR' || response.status !== 'success') {
        throw new Error(
          response.message || `Failed to upload document: ${file.name}`
        );
      }

      const fileData = {
        fileUrl: response.data?.URL || response.data?.data?.URL,
        fileName:
          response.data?.DocumentName || response.data?.data?.DocumentName,
        fileType: response.data?.FileType || response.data?.data?.FileType
      };

      return fileData;
    } catch (uploadError: any) {
      console.error('Error uploading document:', uploadError);

      // Show general error message
      setError(`Failed to upload ${file.name}: ${uploadError.message}`);
    }
  };

  const addFileUpload = () => {
    setUploadedFiles([
      ...uploadedFiles,
      {
        title: `Attachment ${uploadedFiles.length + 1}`,
        file: null
      }
    ]);
  };

  const removeFileUpload = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    if (!bidId) {
      setError('Bid ID is missing');
      return;
    }

    try {
      setLoading(true);

      const departmentID = departments.find(
        (d) => d.DepartmentName === formData.department
      )?.DepartmentID;
      const statusID = statuses.find(
        (s) => s.Status === formData.status
      )?.StatusID;

      // Find CategoryIDs by names
      const categoryIDs = formData.categories
        .map((catName) => {
          const category = categories.find((c) => c.CategoryName === catName);
          return category ? category.CategoryID : null;
        })
        .filter((id) => id !== null) as number[];

      // Check if any files are still uploading
      const hasUploadingFiles =
        uploadedFiles.some((file) => file.isUploading) ||
        formData.bidResultIsUploading ||
        formData.poFileIsUploading;
      if (hasUploadingFiles) {
        setError(
          'Please wait for all file uploads to complete before submitting'
        );
        setLoading(false);
        return;
      }

      // Check for any upload errors
      const hasUploadErrors =
        uploadedFiles.some((file) => file.uploadError) ||
        formData.bidResultError ||
        formData.poFileError;
      if (hasUploadErrors) {
        const confirmContinue = window.confirm(
          'Some files have upload errors. Do you want to continue with the submission anyway?'
        );
        if (!confirmContinue) {
          setLoading(false);
          return;
        }
      }

      // Prepare the document file uploads array INCLUDING bid result and PO files
      const documentFileUploads = [];

      // Add regular uploaded files (from Step 2)
      uploadedFiles
        .filter((file) => file.file || file.fileName)
        .forEach((file) => {
          documentFileUploads.push({
            Type: file.fileType || file.file?.type || '',
            FileName: file.fileName || file.file?.name || '',
            Description: 'Bid Document', // Default to 'Bid Document' for regular files
            BidDocsID: file.id ? parseInt(file.id, 10) : 0
          });
        });

      // Add bid result file if it exists (from Step 1 Award Details)
      if (formData.bidResult || formData.bidResultUrl) {
        documentFileUploads.push({
          Type: formData.bidResult?.type || 'pdf',
          FileName:
            formData.bidResultTitle || formData.bidResult?.name || 'Bid Result',
          Description: 'Bid Result Document', // Specific description for bid result
          BidDocsID: formData.bidResultDocId
            ? parseInt(formData.bidResultDocId, 10)
            : 0
        });
      }

      // Add PO file if it exists (from Step 1 Award Details)
      if (formData.poFile || formData.poFileUrl) {
        documentFileUploads.push({
          Type: formData.poFile?.type || 'pdf',
          FileName:
            formData.poFileTitle || formData.poFile?.name || 'PO Document',
          Description: 'PO Document', // Specific description for PO file
          BidDocsID: formData.poFileDocId
            ? parseInt(formData.poFileDocId, 10)
            : 0
        });
      }

      // Prepare the bid data in the expected API format
      const bidData = {
        Title: formData.title,
        ContractNo: formData.contractNo,
        CategoryIDs: categoryIDs,
        DepartmentID: departmentID,
        OpeningDate: formData.openingDate
          ? formData.openingDate.toISOString()
          : null,
        AdDate: formData.adDate ? formData.adDate.toISOString() : null,

        EffectiveDate: formData.effectiveDate
          ? formData.effectiveDate.toISOString()
          : null,
        StatusID: statusID,
        Description: formData.description,
        AdminNote: formData.adminNotes,
        DocumentFileUpload:
          documentFileUploads.length > 0 ? documentFileUploads : undefined,
        // ADD THESE NEW FIELDS WHEN STATUS IS AWARDED
        ...(formData.status === 'Awarded' && {
          AwardedTo: formData.awardedToContactId,
          PONumber: formData.poNumber
        }),
        SendNotifyProcess: emailData.saveType,
        EmailSubject: emailData.subject,
        EmailBody: emailData.content,
        ...(formData.awardAmount ? { AwardAmount: formData.awardAmount } : {}),
        ...(formData.reopenReason
          ? { ReasonToReopen: formData.reopenReason }
          : {}),
        IsAddendum: formData?.isAddendum
      };

      // ----------------------
      // Create FormData
      // ----------------------
      const formDataToSend = new FormData();

      // Add JSON as string
      formDataToSend.append('data', JSON.stringify(bidData));

      // Create a shallow copy of the array and reverse it
      const reversedFiles = [...uploadedFiles].reverse();

      reversedFiles.forEach((fileItem, index) => {
        if (fileItem.file) {
          formDataToSend.append('DocumentFileUpload', fileItem.file); // file
          formDataToSend.append(
            `DocumentDescription[${index}]`,
            formData?.isAddendum ? 'Child Bid Document' : 'Bid Document'
          ); // meta field
          formDataToSend.append(`DocumentType[${index}]`, fileItem.file.type);
          formDataToSend.append(`DocumentName[${index}]`, fileItem.file.name);
          formDataToSend.append(`BidDocsID[${index}]`, '0');
        }
      });

      // Update the bid details using PATCH method
      const response = await httpRequest(
        'PATCH',
        `/bid_portal/bid_details/update/${bidId}`,
        formDataToSend,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response.type === 'NETWORK_ERROR' || response.status !== 'success') {
        throw new Error(response.message || 'Failed to update bid');
      }

      // Show success message
      toast.success(response.message);

      // Navigate to the bid details page or list on success
      navigate(`/${locationState}/${township}/${uuid}/bid-portal/Admin/home`);
    } catch (submitErr: any) {
      console.error('Error updating bid:', submitErr);
      setError(submitErr.message || 'Failed to update bid');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <BidDetailsForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            types={types}
            categories={categories}
            departments={departments}
            statuses={statuses}
            isGeneratingContractNo={isGeneratingContractNo}
            onBidTypeChange={handleBidTypeChange}
            setError={setError}
            handleFileUploadForPOAndBidResult={
              handleFileUploadForPOAndBidResult
            }
            isFormDisabled={isFormDisabled}
            originalStatus={originalStatus}
          />
        );
      case 1:
        return (
          <FileUploadForm
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            handleFileUpload={handleFileUpload}
            addFileUpload={addFileUpload}
            removeFileUpload={removeFileUpload}
            setError={setError}
          />
        );
      case 2:
        return (
          <EmailNotificationForm
            emailData={emailData}
            setEmailData={setEmailData}
            goLiveDate={null}
            errors={errors}
            setErrors={setErrors}
            status={formData.status}
          />
        );
      default:
        return null;
    }
  };

  if ((loading && activeStep === 0) || loadingDropdowns) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4 }}>
      <Typography
        mb={3}
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: theme.palette.primary.main,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 80,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.primary.main
          }
        }}
      >
        Update Bid
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        {activeStep > 0 && !uiDisabled && (
          <Button onClick={handleBack} sx={{ mr: 1 }} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={uiDisabled}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              loading || uploadedFiles.some((f) => f.isUploading) || uiDisabled
            }
          >
            {loading ? <DotWave /> : 'Submit'}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default BidUpdationForm;
